import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { can } from "@/lib/roles";
import { PageSkeleton } from "@/components/skeletons";
import AuthPage from "@/pages/AuthPage";
import LegalPage from "@/pages/LegalPage";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
// Bundle A-2: static import — a lazy chunk here would keep the old protected tree mounted while it loads.
import AccountDeletedPage from "@/pages/AccountDeletedPage";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import SecurityPage from "@/pages/SecurityPage";
import StatusPage from "@/pages/StatusPage";
import VerifiedPage from "@/pages/VerifiedPage";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const RecapPage = lazy(() => import("@/pages/RecapPage"));
const SettingsPage = lazy(() => import("@/pages/ProfilePage"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const AdminDev = lazy(() => import("@/pages/AdminDev"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const InviteAcceptPage = lazy(() => import("@/pages/InviteAcceptPage"));
const ActivityFeedPage = lazy(() => import("@/pages/ActivityFeedPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const FeedbackBoard = lazy(() => import("@/pages/FeedbackBoard"));
const NotesPage = lazy(() => import("@/pages/NotesPage"));
const MilestonesPage = lazy(() => import("@/pages/MilestonesPage"));
const HabitsPage = lazy(() => import("@/pages/HabitsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const RestoreAccountPage = lazy(() => import("@/pages/RestoreAccountPage"));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Bundle A-1: shadow-owner only; everyone else sees the app's normal 404 page.
function OwnerRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(user, "view_owner_metrics")) return <NotFoundPage />;
  return children;
}

// Bundle A-2: the dev console is for role "admin" or the shadow owner (stored role "owner").
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  if (!can(user, "use_dev_console")) return <Navigate to="/" replace />;
  return children;
}

export default function AppRoutes() {
  const { user } = useAuth();
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account-deleted" element={<AccountDeletedPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/delete-account/confirm" element={<DeleteAccountPage />} />
        <Route path="/restore-account" element={<RestoreAccountPage />} />
        <Route path="/verified" element={<VerifiedPage />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/status" element={<StatusPage />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
        <Route path="/tasks/:taskId" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><RouteErrorBoundary route="analytics"><AnalyticsPage /></RouteErrorBoundary></ProtectedRoute>} />
        <Route path="/recap" element={<ProtectedRoute><RouteErrorBoundary route="recap"><RecapPage /></RouteErrorBoundary></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/settings/:section" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
        <Route path="/admin/dev" element={<AdminRoute><AdminDev /></AdminRoute>} />
        <Route path="/admin" element={<OwnerRoute><AdminPage /></OwnerRoute>} />
        {/* Invite page is public — logged-out users see login/signup
            CTAs that preserve the token, and the page itself displays
            invite metadata (project name, inviter) before auth. */}
        <Route path="/invites/:token" element={<InviteAcceptPage />}/>
        <Route path="/activity" element={<ProtectedRoute><ActivityFeedPage /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        <Route path="/feedback" element={<ProtectedRoute><FeedbackBoard /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
        <Route path="/milestones" element={<ProtectedRoute><MilestonesPage /></ProtectedRoute>} />
        <Route path="/habits" element={<ProtectedRoute><HabitsPage /></ProtectedRoute>} />
        {/* Bundle L PR-2 — semantic redirect: nav label "Work" maps to /tasks */}
        <Route path="/work" element={<Navigate to="/tasks" replace />} />
        {/* Catch-all: any other unmatched URL renders 404 inside the AppShell chrome */}
        <Route path="*" element={<ProtectedRoute><NotFoundPage /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
}
